import {
  COLOR_THEME_PRESETS,
  type ColorThemePresetId,
} from '@core/constants/colorThemePresets';
import {
  findMonacoEditorDefinitionById,
  findPresetMonacoDefinitionById,
  isCustomMonacoEditorThemeId,
  isPresetMonacoThemeId,
  PRESET_MONACO_THEME_DEFINITIONS,
} from './monacoPresetThemeDefinitions';
import {
  diffPresetDefinitionToOverride,
  mergeMonacoPresetThemeOverrides,
  mergePresetMonacoDefinition,
  monacoPresetThemeOverrideHasContent,
  type CustomMonacoEditorThemeId,
  type MonacoPresetThemeOverride,
  type MonacoPresetThemeOverrides,
  type PresetMonacoDefinition,
  type PresetMonacoThemeId,
} from './mergeMonacoPresetTheme';

export interface MonacoEditorThemeMeta {
  backgroundColor: string;
  foregroundColor: string;
}

const PRESET_MONACO_EDITOR_THEME_META = Object.fromEntries(
  PRESET_MONACO_THEME_DEFINITIONS.map(def => [
    def.id,
    {
      backgroundColor: def.backgroundColor,
      foregroundColor: def.foregroundColor,
    },
  ]),
) as Record<
  (typeof PRESET_MONACO_THEME_DEFINITIONS)[number]['id'],
  MonacoEditorThemeMeta
>;

export const MONACO_EDITOR_THEME_IDS = [
  'app-light',
  'app-dark',
  ...PRESET_MONACO_THEME_DEFINITIONS.map(d => d.id),
  'vs',
  'custom-light',
  'vs-dark',
  'custom-dark',
  'hc-black',
] as const;

export type MonacoEditorThemeId = (typeof MONACO_EDITOR_THEME_IDS)[number];

export const MONACO_EDITOR_THEME_META: Record<
  MonacoEditorThemeId,
  MonacoEditorThemeMeta
> = {
  'app-light': {
    backgroundColor: '#ffffff',
    foregroundColor: '#111827',
  },
  'app-dark': {
    backgroundColor: '#27272a',
    foregroundColor: '#e4e4e7',
  },
  ...PRESET_MONACO_EDITOR_THEME_META,
  vs: {
    backgroundColor: '#ffffff',
    foregroundColor: '#000000',
  },
  'custom-light': {
    backgroundColor: '#ffffff',
    foregroundColor: '#000000',
  },
  'vs-dark': {
    backgroundColor: '#1e1e1e',
    foregroundColor: '#d4d4d4',
  },
  'custom-dark': {
    backgroundColor: '#1e1e1e',
    foregroundColor: '#d4d4d4',
  },
  'hc-black': {
    backgroundColor: '#000000',
    foregroundColor: '#ffffff',
  },
};

export type {MonacoPresetThemeOverrides} from './mergeMonacoPresetTheme';

export const DEFAULT_MONACO_EDITOR_THEME: {
  light: MonacoEditorThemeId;
  dark: MonacoEditorThemeId;
} = {
  light: 'vs',
  dark: 'vs-dark',
};

const PRESET_MONACO_LIGHT_IDS = COLOR_THEME_PRESETS.map(
  (p): `preset-${ColorThemePresetId}` => `preset-${p.id}`,
);

const PRESET_MONACO_DARK_IDS = COLOR_THEME_PRESETS.map(
  (p): `preset-${ColorThemePresetId}-dark` => `preset-${p.id}-dark`,
);

/** Themes intended for the app’s light color mode (VS-based light). Excludes legacy `app-light` (still registered for Monaco). */
export const MONACO_EDITOR_LIGHT_THEME_IDS = [
  'vs',
  'custom-light',
  ...PRESET_MONACO_LIGHT_IDS,
] as const;

/** Themes intended for the app’s dark color mode (VS-dark–based). Excludes legacy `app-dark`. */
export const MONACO_EDITOR_DARK_THEME_IDS = [
  'vs-dark',
  'custom-dark',
  ...PRESET_MONACO_DARK_IDS,
  'hc-black',
] as const;

export type MonacoEditorLightThemeId =
  (typeof MONACO_EDITOR_LIGHT_THEME_IDS)[number];

export type MonacoEditorDarkThemeId =
  (typeof MONACO_EDITOR_DARK_THEME_IDS)[number];

export function isMonacoEditorThemeId(id: string): id is MonacoEditorThemeId {
  return (MONACO_EDITOR_THEME_IDS as readonly string[]).includes(id);
}

export function isLightMonacoEditorThemeId(
  id: string,
): id is MonacoEditorLightThemeId {
  return (MONACO_EDITOR_LIGHT_THEME_IDS as readonly string[]).includes(id);
}

export function isDarkMonacoEditorThemeId(
  id: string,
): id is MonacoEditorDarkThemeId {
  return (MONACO_EDITOR_DARK_THEME_IDS as readonly string[]).includes(id);
}

export function normalizeMonacoEditorThemeId(
  id: string | undefined,
  fallback: MonacoEditorThemeId,
): MonacoEditorThemeId {
  return id && isMonacoEditorThemeId(id) ? id : fallback;
}

export function normalizeMonacoEditorThemeIdForLightMode(
  id: string | undefined,
  fallback: MonacoEditorLightThemeId = DEFAULT_MONACO_EDITOR_THEME.light as MonacoEditorLightThemeId,
): MonacoEditorLightThemeId {
  if (id === 'app-light') {
    return 'vs';
  }
  if (id && isLightMonacoEditorThemeId(id)) {
    return id;
  }
  return fallback;
}

export function normalizeMonacoEditorThemeIdForDarkMode(
  id: string | undefined,
  fallback: MonacoEditorDarkThemeId = DEFAULT_MONACO_EDITOR_THEME.dark as MonacoEditorDarkThemeId,
): MonacoEditorDarkThemeId {
  if (id === 'app-dark') {
    return 'vs-dark';
  }
  /** Legacy: dark midnight was stored as `preset-midnight` before `preset-midnight` became the light variant. */
  if (id === 'preset-midnight') {
    return 'preset-midnight-dark';
  }
  if (id && isDarkMonacoEditorThemeId(id)) {
    return id;
  }
  return fallback;
}

export interface EditorThemeSettings {
  light: MonacoEditorThemeId;
  dark: MonacoEditorThemeId;
}

export function mergeEditorThemeSettings(
  input?: Partial<EditorThemeSettings> | null,
): EditorThemeSettings {
  return {
    light: normalizeMonacoEditorThemeIdForLightMode(
      input?.light,
      DEFAULT_MONACO_EDITOR_THEME.light as MonacoEditorLightThemeId,
    ),
    dark: normalizeMonacoEditorThemeIdForDarkMode(
      input?.dark,
      DEFAULT_MONACO_EDITOR_THEME.dark as MonacoEditorDarkThemeId,
    ),
  };
}

/**
 * Keeps only overrides for the selected light/dark editor themes (palette preset
 * or custom-light/custom-dark). Other themes drop stored override entries.
 */
export function filterMonacoPresetOverridesToActiveEditorThemes(
  overrides: MonacoPresetThemeOverrides | undefined,
  editorTheme: EditorThemeSettings,
): MonacoPresetThemeOverrides {
  const merged = mergeMonacoPresetThemeOverrides(overrides);
  const out: MonacoPresetThemeOverrides = {};
  const {light, dark} = editorTheme;
  if (
    (isPresetMonacoThemeId(light) || isCustomMonacoEditorThemeId(light)) &&
    merged[light]
  ) {
    out[light] = merged[light]!;
  }
  if (
    (isPresetMonacoThemeId(dark) || isCustomMonacoEditorThemeId(dark)) &&
    merged[dark]
  ) {
    out[dark] = merged[dark]!;
  }
  return out;
}

/** Drop stored Monaco overrides keyed by palette preset theme ids (`preset-*`). */
function stripPresetMonacoThemeKeysFromOverrides(
  merged: MonacoPresetThemeOverrides,
): MonacoPresetThemeOverrides {
  const cleaned: MonacoPresetThemeOverrides = {...merged};
  for (const k of Object.keys(cleaned)) {
    if (isPresetMonacoThemeId(k)) {
      delete cleaned[k];
    }
  }
  return cleaned;
}

/** Spread fragment for persisted `unifiedDiffView` from a preset slot’s merged row. */
function unifiedDiffViewFromPresetMergedSlot(
  slotMerged?: MonacoPresetThemeOverride | null,
): Pick<MonacoPresetThemeOverride, 'unifiedDiffView'> | Record<string, never> {
  const udv = slotMerged?.unifiedDiffView;
  if (!udv || Object.keys(udv).length === 0) {
    return {};
  }
  return {unifiedDiffView: udv};
}

function presetSlotToCustomOverride(
  presetSlotId: PresetMonacoThemeId,
  customBase: PresetMonacoDefinition,
  merged: MonacoPresetThemeOverrides,
): MonacoPresetThemeOverride {
  const presetDef = findPresetMonacoDefinitionById(presetSlotId);
  if (!presetDef) {
    throw new Error(
      `monacoEditorTheme: preset definition for ${presetSlotId} not found`,
    );
  }
  const slotMerged = merged[presetSlotId] ?? null;
  const eff = mergePresetMonacoDefinition(presetDef, slotMerged);
  const diff = diffPresetDefinitionToOverride(customBase, eff);
  return {
    ...diff,
    ...unifiedDiffViewFromPresetMergedSlot(merged[presetSlotId]),
  };
}

/**
 * When the active slot uses a palette preset id, fold it into the canonical `custom-*`
 * override row; otherwise carry forward an existing non-empty `custom-*` row.
 */
function reuseOrBuildCustomSlotOverride(
  slotThemeId: MonacoEditorThemeId,
  customKey: CustomMonacoEditorThemeId,
  customBase: PresetMonacoDefinition,
  merged: MonacoPresetThemeOverrides,
): MonacoPresetThemeOverride | undefined {
  if (isPresetMonacoThemeId(slotThemeId)) {
    return presetSlotToCustomOverride(slotThemeId, customBase, merged);
  }
  const existing = merged[customKey];
  if (existing && monacoPresetThemeOverrideHasContent(existing)) {
    return existing;
  }
  return undefined;
}

function pruneOverridesWithContent(
  next: MonacoPresetThemeOverrides,
): MonacoPresetThemeOverrides {
  const pruned: MonacoPresetThemeOverrides = {};
  for (const [k, v] of Object.entries(next)) {
    if (v && monacoPresetThemeOverrideHasContent(v)) {
      pruned[k as keyof MonacoPresetThemeOverrides] = v;
    }
  }
  return pruned;
}

/** After migration, use `custom-*` as the saved theme id if the slot was preset-based or pruned overrides still hold content. */
function slotUsesPersistedCustomTheme(
  slotThemeId: MonacoEditorThemeId,
  pruned: MonacoPresetThemeOverrides,
  customKey: CustomMonacoEditorThemeId,
): boolean {
  if (isPresetMonacoThemeId(slotThemeId)) {
    return true;
  }
  const row = pruned[customKey];
  return !!(row && monacoPresetThemeOverrideHasContent(row));
}

/**
 * Normalizes style.json: palette-preset Monaco ids and preset-* override keys are
 * folded into `custom-light` / `custom-dark` + `editorTheme` so saved editor
 * colors are not tied to a color-palette preset id.
 */
export function migrateEditorThemeStorageToCustom(
  editorTheme: EditorThemeSettings,
  overrides: MonacoPresetThemeOverrides,
): {
  editorTheme: EditorThemeSettings;
  overrides: MonacoPresetThemeOverrides;
} {
  const merged = mergeMonacoPresetThemeOverrides(overrides);
  const {light, dark} = editorTheme;

  const hasPresetSlots =
    isPresetMonacoThemeId(light) || isPresetMonacoThemeId(dark);
  const hasPresetOverrideKeys = Object.keys(merged).some(isPresetMonacoThemeId);

  if (!hasPresetSlots && !hasPresetOverrideKeys) {
    return {
      editorTheme,
      overrides: stripPresetMonacoThemeKeysFromOverrides(merged),
    };
  }

  const customLightBase = findMonacoEditorDefinitionById('custom-light');
  const customDarkBase = findMonacoEditorDefinitionById('custom-dark');
  if (!customLightBase || !customDarkBase) {
    throw new Error(
      'monacoEditorTheme: custom-light/custom-dark definitions are missing; check preset Monaco themes.',
    );
  }

  const next: MonacoPresetThemeOverrides = {};
  const lightOv = reuseOrBuildCustomSlotOverride(
    light,
    'custom-light',
    customLightBase,
    merged,
  );
  if (lightOv !== undefined) {
    next['custom-light'] = lightOv;
  }
  const darkOv = reuseOrBuildCustomSlotOverride(
    dark,
    'custom-dark',
    customDarkBase,
    merged,
  );
  if (darkOv !== undefined) {
    next['custom-dark'] = darkOv;
  }

  const pruned = pruneOverridesWithContent(next);

  return {
    editorTheme: {
      light: slotUsesPersistedCustomTheme(light, pruned, 'custom-light')
        ? 'custom-light'
        : light,
      dark: slotUsesPersistedCustomTheme(dark, pruned, 'custom-dark')
        ? 'custom-dark'
        : dark,
    },
    overrides: pruned,
  };
}

export function getMonacoEditorThemeMeta(
  id: string | undefined,
  fallback: MonacoEditorThemeId,
  presetOverrides?: MonacoPresetThemeOverrides | null,
): MonacoEditorThemeMeta {
  const normalizedThemeId = normalizeMonacoEditorThemeId(id, fallback);
  if (
    isPresetMonacoThemeId(normalizedThemeId) ||
    isCustomMonacoEditorThemeId(normalizedThemeId)
  ) {
    const def = findMonacoEditorDefinitionById(normalizedThemeId);
    if (def) {
      const merged = mergePresetMonacoDefinition(
        def,
        presetOverrides?.[normalizedThemeId] ?? null,
      );
      return {
        backgroundColor: merged.backgroundColor,
        foregroundColor: merged.foregroundColor,
      };
    }
  }
  return MONACO_EDITOR_THEME_META[normalizedThemeId];
}
